import React, { forwardRef, useContext, useLayoutEffect } from "react";
import { cn } from "../../lib/utils.js";
import { CARD_EDGE_PADDING, useCardSize } from "./card-size.js";
import { CardStickyContext } from "./card-sticky.js";

/* `title` is the heading, so the native title attribute (a tooltip) is left
   out, as on Alert. */
export interface CardHeaderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  /** The card's heading: an h2, or an h3 with `headingLevel={3}`. */
  title?: React.ReactNode;
  /** One muted line under the title (phrasing content). */
  description?: React.ReactNode;
  /** Media at the row's leading edge, before the title (an icon tile). */
  leading?: React.ReactNode;
  /** The trailing action group at the row's end (a button, a menu). */
  actionsSlot?: React.ReactNode;
  /** Level of the `title` heading. 2 (default) for a card on a page headed
   *  h1; 3 for a card inside a section headed h2. */
  headingLevel?: 2 | 3;
  /** The hairline under the header. Default true. */
  divided?: boolean;
  /** Pin the header to the top of the nearest scrolling ancestor, on the card
   *  fill with the card's top corners. The Card stops clipping while a sticky
   *  header is inside it. */
  sticky?: boolean;
}

const has = (node: React.ReactNode) => node != null && node !== false;

/* rounded-t-[inherit] takes the Card's own corner (14px, or 10px on an inset
   card), so the pinned fill never pokes past the card's rounding. */
const STICKY = "sticky top-0 z-raised rounded-t-[inherit] bg-pb-card";

/** The card's title row. Its padding follows the Card's `size`. With only
 *  children it renders them as they are; with `title`, `description`,
 *  `leading` or `actionsSlot` it lays them out as leading media, a title
 *  column (children follow the description) and the trailing actions. */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  function CardHeader(
    {
      className,
      children,
      title,
      description,
      leading,
      actionsSlot,
      headingLevel = 2,
      divided = true,
      sticky = false,
      ...rest
    },
    ref,
  ) {
    const size = useCardSize();
    const registerSticky = useContext(CardStickyContext);
    // Layout effect, so the card unclips before the first paint.
    useLayoutEffect(() => {
      if (!sticky || !registerSticky) return undefined;
      return registerSticky();
    }, [sticky, registerSticky]);

    const slotted =
      has(title) || has(description) || has(leading) || has(actionsSlot);
    const Heading = headingLevel === 3 ? "h3" : "h2";
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between gap-3",
          divided && "border-b border-pb-border/70",
          CARD_EDGE_PADDING[size],
          sticky && STICKY,
          className,
        )}
        {...rest}
      >
        {slotted ? (
          <>
            {has(leading) ? (
              <div className="flex shrink-0">{leading}</div>
            ) : null}
            <div className="min-w-0 flex-1">
              {has(title) ? (
                <Heading className="text-sm font-semibold text-pb-text">
                  {title}
                </Heading>
              ) : null}
              {has(description) ? (
                <p className="mt-0.5 text-dense-1 text-pb-text-muted">
                  {description}
                </p>
              ) : null}
              {children}
            </div>
            {has(actionsSlot) ? (
              <div className="flex shrink-0 items-center gap-2">
                {actionsSlot}
              </div>
            ) : null}
          </>
        ) : (
          children
        )}
      </div>
    );
  },
);
